import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  cart: {},
  order: {} // cart[eventId] will be an object
};

const cartbodySlice = createSlice({
  name: 'cartbody',
  initialState,
  reducers: {
    addServiceToCart: (state, action) => {
      const { eveId, eventname, type, event_start_date, item } = action.payload;
      const eventId = Number(eveId);

      // Update allowed types to include venueData
      if (!['priestData', 'tentHouseData', 'cateringData', 'venueData'].includes(type)) return;

      if (!state.cart[eventId]) {
        state.cart[eventId] = {
          eventname: eventname || '',
          event_start_date: event_start_date || "",
          priestData: [],
          tentHouseData: [],
          cateringData: [],
          venueData: [],
        };
      }

      const list = state.cart[eventId][type];
      const itemKey = item.provider_id || item.id;

      const existingItemIndex = list.findIndex(
        (i) => (i.provider_id || i.id) === itemKey
      );

      // Prepare item fields based on type
      if (type === 'tentHouseData') {
        item.provider_id = item.provider_id;
        item.providername = item.providername;
        item.services = item.services || [];
        item.Pricing = item.Pricing || {};
        item.balance_to_be_paid = item.balance_to_be_paid;
        item.is_checked_out = item.is_checked_out;
        item.is_edit_allowed = item.is_edit_allowed;
        item.paid_amount = item.paid_amount;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;
        item.event_start_date = item.event_start_date;
        item.pricing = item.pricing;

        item.services.forEach((service) => {
          service.mapping_id = service.mapping_id;
          service.service_id = service.service_id;
          service.cart_id = service.cart_id;
          service.quantity = service.quantity;
          service.price = service.price;
          service.discountPrice = service.discountPrice !== undefined ? service.discountPrice : null;
          service.validUpto = service.validUpto !== undefined ? service.validUpto : null;
        });
      }

      if (type === 'cateringData') {
        item.provider_id = item.provider_id;
        item.providername = item.providername;
        item.services = item.services || [];
        item.balance_to_be_paid = item.balance_to_be_paid;
        item.is_checked_out = item.is_checked_out;
        item.is_edit_allowed = item.is_edit_allowed;
        item.paid_amount = item.paid_amount;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;

        item.services.forEach((service) => {
          service.mapping_id = service.mapping_id;
          service.service_id = service.service_id;
          service.quantity = service.quantity;
          service.price = service.price;
          service.discountPrice = service.discountPrice !== undefined ? service.discountPrice : null;
          service.validUpto = service.validUpto !== undefined ? service.validUpto : null;
          service.category = service.category;
          service.mealtype = service.mealtype;
          service.cuisine = service.cuisine;
          service.gst_amount = service.gst_amount;
          service.gst_percentage = service.gst_percentage;
          service.total_amount = service.total_amount;
        });
      }

      if (type === 'priestData') {
        item.cart_id = item.cart_id;
        item.service_id = item.service_id;
        item.price = item.price;
        item.discountPrice = item.discountPrice !== undefined ? item.discountPrice : null;
        item.validUpto = item.validUpto !== undefined ? item.validUpto : null;
        item.is_checked_out = item.is_checked_out !== undefined ? item.is_checked_out : false;
        item.is_edit_allowed = item.is_edit_allowed !== undefined ? item.is_edit_allowed : false;
        item.paid_amount = item.paid_amount !== undefined ? item.paid_amount : 0;
        item.balance_to_be_paid = item.balance_to_be_paid !== undefined ? item.balance_to_be_paid : 0;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;
        item.gstAmount = item.gstAmount;
        item.gstPercentage = item.gstPercentage;
        item.totalAmount = item.totalAmount;
      }

      // Venue Data Processing
      if (type === 'venueData') {
        item.id = item.id;
        item.cartId = item.cartId;
        item.venueId = item.venueId;
        item.spaceId = item.spaceId;
        item.provider_id = item.providerId || item.provider_id;
        item.provider_name = item.provider_name || item.provider?.name || '';
        item.provider_mobile = item.provider_mobile || item.provider?.mobileNumber || '';
        item.provider_email = item.provider_email || item.provider?.emailId || '';
        item.venue_name = item.venue_name || item.venue?.name || '';
        item.venue_address = item.venue_address || item.venue?.address || '';
        item.venue_description = item.venue_description || item.venue?.description || '';
        item.section_name = item.section_name || item.section?.name || '';
        item.section_capacity = item.section_capacity || item.section?.capacity || '';
        item.pricing_type = item.pricing_type || item.pricingType;
        item.food_included = item.food_included !== undefined ? item.food_included : item.foodIncluded;
        item.outside_food_allowed = item.outside_food_allowed !== undefined ? item.outside_food_allowed : item.outsideFoodAllowed;
        item.no_of_guests = item.no_of_guests || item.noOfGuests || item.guest_count;
        item.from_date = item.from_date || item.fromDate;
        item.from_time = item.from_time || item.fromTime || item.start_time;
        item.to_date = item.to_date || item.toDate;
        item.to_time = item.to_time || item.toTime || item.end_time;
        item.rules_accepted = item.rules_accepted !== undefined ? item.rules_accepted : item.rulesAccepted;
        item.base_price = item.base_price || item.basePrice;
        item.food_total = item.food_total || item.foodTotal;
        item.drink_total = item.drink_total || item.drinkTotal;
        item.subtotal = item.subtotal;
        item.gst_percentage = item.gst_percentage || item.gstPercentage;
        item.gst_amount = item.gst_amount || item.gstAmount;
        item.total_amount = item.total_amount || item.totalAmount;
        item.paid_amount = item.paid_amount;
        item.balance_to_be_paid = item.balance_to_be_paid || item.balanceToBePaid;
        item.is_checked_out = item.is_checked_out !== undefined ? item.is_checked_out : item.isCheckedOut;
        item.is_edit_allowed = item.is_edit_allowed !== undefined ? item.is_edit_allowed : item.isEditAllowed;
        item.is_canceled = item.is_canceled !== undefined ? item.is_canceled : item.isCanceled;
        item.is_pay_later = item.is_pay_later !== undefined ? item.is_pay_later : item.isPayLater;
        item.refunded_amount = item.refunded_amount || item.refundedAmount;
        item.booking_date = item.booking_date;
        item.start_time = item.start_time || item.fromTime || item.from_time;
        item.end_time = item.end_time || item.toTime || item.to_time;
        item.guest_count = item.guest_count || item.no_of_guests || item.noOfGuests;
        item.foodSelections = item.foodSelections || [];
        item.drinkSelections = item.drinkSelections || [];
        item.pricing = item.pricing;
        item.paymentSummary = item.paymentSummary;
        
        // Create services array for compatibility with other components
        item.services = item.services || [{
          ...item,
          id: item.id,
          cart_id: item.cartId,
          price: item.subtotal,
          discountPrice: item.subtotal,
          validUpto: null,
          quantity: 1,
          service_name: `Venue: ${item.venue_name || item.venue?.name || ''} - ${item.section_name || item.section?.name || ''}`,
        }];
        
        // Calculate total if not present
        item.total = item.total_amount || item.totalAmount || item.subtotal || 0;
      }

      if (existingItemIndex === -1) {
        list.push(item);
      } else {
        list[existingItemIndex] = item;
      }
    },

    addServiceToOrder: (state, action) => {
      const { eveId, eventname, type, event_start_date, item } = action.payload;
      const eventId = Number(eveId);

      if (!['priestData', 'tentHouseData', 'cateringData', 'venueData'].includes(type)) return;

      if (!state.order[eventId]) {
        state.order[eventId] = {
          eventname: eventname || '',
          event_start_date: event_start_date || "",
          priestData: [],
          tentHouseData: [],
          cateringData: [],
          venueData: [],
        };
      }

      const list = state.order[eventId][type];
      const itemKey = item.provider_id || item.id;

      const existingItemIndex = list.findIndex(
        (i) => (i.provider_id || i.id) === itemKey
      );

      // Prepare item fields based on type (similar to addServiceToCart)
      if (type === 'tentHouseData') {
        item.provider_id = item.provider_id;
        item.providername = item.providername;
        item.services = item.services || [];
        item.Pricing = item.Pricing || {};
        item.balance_to_be_paid = item.balance_to_be_paid;
        item.is_checked_out = item.is_checked_out;
        item.is_edit_allowed = item.is_edit_allowed;
        item.paid_amount = item.paid_amount;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;
        item.event_start_date = item.event_start_date;
        item.pricing = item.pricing;

        item.services.forEach((service) => {
          service.mapping_id = service.mapping_id;
          service.service_id = service.service_id;
          service.order_id = service.order_id;
          service.quantity = service.quantity;
          service.price = service.price;
          service.discountPrice = service.discountPrice !== undefined ? service.discountPrice : null;
          service.validUpto = service.validUpto !== undefined ? service.validUpto : null;
        });
      }

      if (type === 'cateringData') {
        item.provider_id = item.provider_id;
        item.providername = item.providername;
        item.services = item.services || [];
        item.balance_to_be_paid = item.balance_to_be_paid;
        item.is_checked_out = item.is_checked_out;
        item.is_edit_allowed = item.is_edit_allowed;
        item.paid_amount = item.paid_amount;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;

        item.services.forEach((service) => {
          service.mapping_id = service.mapping_id;
          service.service_id = service.service_id;
          service.quantity = service.quantity;
          service.price = service.price;
          service.discountPrice = service.discountPrice !== undefined ? service.discountPrice : null;
          service.validUpto = service.validUpto !== undefined ? service.validUpto : null;
          service.category = service.category;
          service.mealtype = service.mealtype;
          service.cuisine = service.cuisine;
          service.gst_amount = service.gst_amount;
          service.gst_percentage = service.gst_percentage;
          service.total_amount = service.total_amount;
        });
      }

      if (type === 'priestData') {
        item.order_id = item.order_id;
        item.service_id = item.service_id;
        item.price = item.price;
        item.discountPrice = item.discountPrice !== undefined ? item.discountPrice : null;
        item.validUpto = item.validUpto !== undefined ? item.validUpto : null;
        item.is_checked_out = item.is_checked_out !== undefined ? item.is_checked_out : false;
        item.is_edit_allowed = item.is_edit_allowed !== undefined ? item.is_edit_allowed : false;
        item.paid_amount = item.paid_amount !== undefined ? item.paid_amount : 0;
        item.balance_to_be_paid = item.balance_to_be_paid !== undefined ? item.balance_to_be_paid : 0;
        item.is_cancel_in_progress = item.is_cancel_in_progress;
        item.is_canceled = item.is_canceled;
        item.refund_complete = item.refund_complete;
        item.refund_in_pending = item.refund_in_pending;
        item.refunded_amount = item.refunded_amount;
        item.gstAmount = item.gstAmount;
        item.gstPercentage = item.gstPercentage;
        item.totalAmount = item.totalAmount;
      }

      if (type === 'venueData') {
        // Same processing as addServiceToCart for venue
        item.id = item.id;
        item.cartId = item.cartId;
        item.venueId = item.venueId;
        item.spaceId = item.spaceId;
        item.provider_id = item.providerId || item.provider_id;
        item.provider_name = item.provider_name || item.provider?.name || '';
        item.provider_mobile = item.provider_mobile || item.provider?.mobileNumber || '';
        item.provider_email = item.provider_email || item.provider?.emailId || '';
        item.venue_name = item.venue_name || item.venue?.name || '';
        item.venue_address = item.venue_address || item.venue?.address || '';
        item.venue_description = item.venue_description || item.venue?.description || '';
        item.section_name = item.section_name || item.section?.name || '';
        item.section_capacity = item.section_capacity || item.section?.capacity || '';
        item.pricing_type = item.pricing_type || item.pricingType;
        item.food_included = item.food_included !== undefined ? item.food_included : item.foodIncluded;
        item.outside_food_allowed = item.outside_food_allowed !== undefined ? item.outside_food_allowed : item.outsideFoodAllowed;
        item.no_of_guests = item.no_of_guests || item.noOfGuests || item.guest_count;
        item.from_date = item.from_date || item.fromDate;
        item.from_time = item.from_time || item.fromTime || item.start_time;
        item.to_date = item.to_date || item.toDate;
        item.to_time = item.to_time || item.toTime || item.end_time;
        item.rules_accepted = item.rules_accepted !== undefined ? item.rules_accepted : item.rulesAccepted;
        item.base_price = item.base_price || item.basePrice;
        item.food_total = item.food_total || item.foodTotal;
        item.drink_total = item.drink_total || item.drinkTotal;
        item.subtotal = item.subtotal;
        item.gst_percentage = item.gst_percentage || item.gstPercentage;
        item.gst_amount = item.gst_amount || item.gstAmount;
        item.total_amount = item.total_amount || item.totalAmount;
        item.paid_amount = item.paid_amount;
        item.balance_to_be_paid = item.balance_to_be_paid || item.balanceToBePaid;
        item.is_checked_out = item.is_checked_out !== undefined ? item.is_checked_out : item.isCheckedOut;
        item.is_edit_allowed = item.is_edit_allowed !== undefined ? item.is_edit_allowed : item.isEditAllowed;
        item.is_canceled = item.is_canceled !== undefined ? item.is_canceled : item.isCanceled;
        item.is_pay_later = item.is_pay_later !== undefined ? item.is_pay_later : item.isPayLater;
        item.refunded_amount = item.refunded_amount || item.refundedAmount;
        item.booking_date = item.booking_date;
        item.start_time = item.start_time || item.fromTime || item.from_time;
        item.end_time = item.end_time || item.toTime || item.to_time;
        item.guest_count = item.guest_count || item.no_of_guests || item.noOfGuests;
        item.foodSelections = item.foodSelections || [];
        item.drinkSelections = item.drinkSelections || [];
        item.pricing = item.pricing;
        item.paymentSummary = item.paymentSummary;
        item.services = item.services || [{
          ...item,
          id: item.id,
          order_id: item.order_id,
          price: item.subtotal,
          discountPrice: item.subtotal,
          validUpto: null,
          quantity: 1,
          service_name: `Venue: ${item.venue_name || item.venue?.name || ''} - ${item.section_name || item.section?.name || ''}`,
        }];
        item.total = item.total_amount || item.totalAmount || item.subtotal || 0;
      }

      if (existingItemIndex === -1) {
        list.push(item);
      } else {
        list[existingItemIndex] = item;
      }
    },

    removeAllCateringData: (state, action) => {
      const { eventId } = action.payload;
      const eid = Number(eventId);

      if (state.cart[eid]) {
        state.cart[eid].cateringData = [];
      }
    },

    removeCateringServicesByProviderId: (state, action) => {
      const { eventId, providerId } = action.payload;
      const eid = Number(eventId);

      if (!state.cart[eid]?.cateringData) return;

      const updatedProviders = state.cart[eid].cateringData
        .map(provider => {
          if (provider.provider_id !== providerId) return provider;
          return {
            ...provider,
            services: [],
          };
        })
        .filter(provider => provider.services.length > 0);

      state.cart[eid].cateringData = updatedProviders;
    },

    removeServiceFromCart: (state, action) => {
      const { eventId, type, itemId } = action.payload;
      const eid = Number(eventId);
      if (state.cart[eid]) {
        state.cart[eid][type] = state.cart[eid][type].filter(
          (item) => (item.provider_id || item.id) !== itemId
        );
      }
    },

    removeCateringServiceByMappingId: (state, action) => {
      const { eventId, providerId, mappingId } = action.payload;
      const eid = Number(eventId);

      if (!state.cart[eid]?.cateringData) return;

      const updatedProviders = state.cart[eid].cateringData
        .map((provider) => {
          if (provider.provider_id !== providerId) return provider;

          const updatedServices = provider.services.filter(
            (service) => service.mapping_id !== mappingId
          );

          return {
            ...provider,
            services: updatedServices,
          };
        })
        .filter((provider) => provider.services.length > 0);

      state.cart[eid].cateringData = updatedProviders;
    },

    removePriestByCartId: (state, action) => {
      const { eventId, cartId } = action.payload;
      const eid = Number(eventId);
      if (state.cart[eid] && Array.isArray(state.cart[eid].priestData)) {
        state.cart[eid].priestData = state.cart[eid].priestData.filter(
          (item) => item.cart_id !== cartId
        );
      }
    },

    removeTentHouseByProviderId: (state, action) => {
      const { eventId, providerId } = action.payload;
      const eid = Number(eventId);
      if (state.cart[eid] && Array.isArray(state.cart[eid].tentHouseData)) {
        state.cart[eid].tentHouseData = state.cart[eid].tentHouseData.filter(
          (item) => item.provider_id !== providerId
        );
      }
    },

    // ============================================
    // VENUE REDUCERS
    // ============================================

    addVenueToCart: (state, action) => {
      const { eveId, item } = action.payload;
      const eventId = Number(eveId);

      if (!state.cart[eventId]) {
        state.cart[eventId] = {
          eventname: item.eventname || '',
          event_start_date: item.event_start_date || '',
          priestData: [],
          tentHouseData: [],
          cateringData: [],
          venueData: [],
        };
      }

      if (!state.cart[eventId].venueData) {
        state.cart[eventId].venueData = [];
      }

      // Check if venue already exists
      const existingIndex = state.cart[eventId].venueData.findIndex(
        v => v.id === item.id
      );

      if (existingIndex === -1) {
        state.cart[eventId].venueData.push(item);
      } else {
        state.cart[eventId].venueData[existingIndex] = { 
          ...state.cart[eventId].venueData[existingIndex], 
          ...item 
        };
      }
    },

    // Remove venue from cart
    removeVenueByProviderId: (state, action) => {
      const { cartItemId } = action.payload;
      Object.keys(state.cart).forEach(eventId => {
        if (state.cart[eventId]?.venueData) {
          state.cart[eventId].venueData = state.cart[eventId].venueData.filter(
            item => item.id !== cartItemId && item.cartId !== cartItemId
          );
        }
      });
    },

    // Update venue in cart
    updateVenueInCart: (state, action) => {
      const { eveId, item } = action.payload;
      const eventId = Number(eveId);
      
      if (state.cart[eventId] && state.cart[eventId].venueData) {
        const index = state.cart[eventId].venueData.findIndex(
          v => v.id === item.id
        );
        if (index !== -1) {
          state.cart[eventId].venueData[index] = { 
            ...state.cart[eventId].venueData[index], 
            ...item 
          };
        }
      }
    },

    // Clear all venue data for an event
    clearVenueData: (state, action) => {
      const { eventId } = action.payload;
      const eid = Number(eventId);
      
      if (state.cart[eid] && state.cart[eid].venueData) {
        state.cart[eid].venueData = [];
      }
    },

    clearEventCart: (state, action) => {
      const eid = Number(action.payload);
      delete state.cart[eid];
    },

    clearEntireCart: (state) => {
      state.cart = {};
    },

    clearEntireOrder: (state) => {
      state.order = {};
    },
  },
});

export const {
  addServiceToCart,
  addServiceToOrder,
  clearEntireOrder,
  removeServiceFromCart,
  clearEventCart,
  clearEntireCart,
  removeCateringServiceByMappingId,
  removeAllCateringData,
  removePriestByCartId,
  removeCateringServicesByProviderId,
  removeTentHouseByProviderId,
  // Venue exports
  addVenueToCart,
  removeVenueByProviderId,
  updateVenueInCart,
  clearVenueData,
} = cartbodySlice.actions;

export default cartbodySlice.reducer;

// ============================================
// SELECTORS
// ============================================

// Count cart items including venue
export const selectCartItemsCount = (state) => {
  const cartData = state.cartbodyslice.cart;
  const eventDataMap = state.eventcartdata?.eventDetailsById || {};

  let totalCount = 0;
  const now = new Date();

  Object.entries(cartData).forEach(([eventId, eventDetails]) => {
    const hasCartData = !!cartData[eventId];
    const hasEventData = !!eventDataMap[eventId];

    if (!hasCartData || !hasEventData) {
      return;
    }

    const {
      priestData,
      tentHouseData,
      cateringData,
      venueData,
    } = eventDetails;

    // Filter out checked out items
    const activePriestData = priestData?.filter(item => !item.is_checked_out) || [];
    const activeTentData = tentHouseData?.filter(item => !item.is_checked_out) || [];
    const activeCateringData = cateringData?.filter(item => !item.is_checked_out) || [];
    const activeVenueData = venueData?.filter(item => !item.is_checked_out) || [];

    // Get event date from eventDataMap
    const eventdate = eventDataMap[eventId]?.startDate;
    const date = eventdate ? new Date(eventdate) : null;
    const isEventOver = date ? date < now : false;

    if (isEventOver) {
      return;
    }

    // Skip if all services are empty
    if (activePriestData.length === 0 && activeTentData.length === 0 && 
        activeCateringData.length === 0 && activeVenueData.length === 0) {
      return;
    }

    // Count items
    const eventCount = activePriestData.length + activeTentData.length + 
                       activeCateringData.length + activeVenueData.length;
    totalCount += eventCount;
  });

  return totalCount;
};

// Get all identifiers by event ID (including venue)
export const selectIdentifiersByEventId = (state, eventId) => {
  const eid = Number(eventId);
  const cartEntry = state.cartbodyslice.cart[eid];
  if (!cartEntry) return null;

  // Priest
  const priestCartIds = cartEntry.priestData?.map(item => item.cart_id) || [];

  // Tenthouse
  const tentProviderIds = cartEntry.tentHouseData?.map(item => item.provider_id) || [];

  // Catering
  const cateringDetails = cartEntry.cateringData?.flatMap(provider =>
    provider.services.map(service => ({
      providerId: provider.provider_id,
      mappingId: service.mapping_id,
      cartId: service.cart_id,
    }))
  ) || [];

  // Venue
  const venueDetails = cartEntry.venueData?.map(item => ({
    id: item.id,
    cartId: item.cartId || item.id,
    venueId: item.venueId,
    spaceId: item.spaceId,
    providerId: item.provider_id || item.providerId,
    totalAmount: item.total_amount || item.totalAmount || 0,
  })) || [];

  return {
    priestCartIds,
    tentProviderIds,
    cateringDetails,
    venueDetails,
  };
};

// Get total amount for an event including all services
export const selectEventTotalAmount = (state, eventId) => {
  const eid = Number(eventId);
  const cartEntry = state.cartbodyslice.cart[eid];
  if (!cartEntry) return 0;

  let total = 0;

  // Priest
  cartEntry.priestData?.forEach(item => {
    if (!item.is_checked_out) {
      total += item.totalAmount || item.price || 0;
    }
  });

  // Tenthouse
  cartEntry.tentHouseData?.forEach(provider => {
    if (!provider.is_checked_out) {
      provider.services?.forEach(service => {
        total += (service.price || 0) * (service.quantity || 1);
      });
    }
  });

  // Catering
  cartEntry.cateringData?.forEach(provider => {
    if (!provider.is_checked_out) {
      provider.services?.forEach(service => {
        total += (service.price || 0) * (service.quantity || 1);
      });
    }
  });

  // Venue
  cartEntry.venueData?.forEach(item => {
    if (!item.is_checked_out) {
      total += parseFloat(item.total_amount || item.totalAmount || item.subtotal || 0);
    }
  });

  return total;
};

// Check if event has venue in cart
export const hasVenueInCart = (state, eventId) => {
  const eid = Number(eventId);
  const cartEntry = state.cartbodyslice.cart[eid];
  if (!cartEntry) return false;
  
  const activeVenueData = cartEntry.venueData?.filter(item => !item.is_checked_out) || [];
  return activeVenueData.length > 0;
};

// Get all venue items for an event
export const selectVenueItemsByEventId = (state, eventId) => {
  const eid = Number(eventId);
  const cartEntry = state.cartbodyslice.cart[eid];
  if (!cartEntry) return [];
  
  return cartEntry.venueData?.filter(item => !item.is_checked_out) || [];
};